import * as THREE from 'three';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { terrainCellAt, terrainLatticeHeight, terrainMesh } from '../../../shared/game/terrain.js';

/** Samples the painted triangle colours at creation time, so ground cover inherits the land's local tone and grade. */
export function groundColorSampler(map: MapDefinition, geometry: THREE.BufferGeometry) {
  const flat = (geometry as THREE.PlaneGeometry).parameters;
  const { columns, rows } = map.terrain ? terrainMesh(map) : { columns: flat.widthSegments, rows: flat.heightSegments };
  const dx = map.width / columns, dz = map.depth / rows;
  const positions = geometry.getAttribute('position'), colors = geometry.getAttribute('color');
  const ids = new Int32Array((columns + 1) * (rows + 1)).fill(-1);
  for (let k = 0; k < positions.count; k++) {
    const i = Math.round((positions.getX(k) + map.width / 2) / dx), j = Math.round((positions.getZ(k) + map.depth / 2) / dz);
    ids[j * (columns + 1) + i] = k;
  }
  const a = new THREE.Color(), b = new THREE.Color(), c = new THREE.Color();
  const read = (target: THREE.Color, i: number, j: number) => target.fromBufferAttribute(colors, ids[j * (columns + 1) + i]);
  return (x: number, z: number, target: THREE.Color) => {
    const gx = THREE.MathUtils.clamp((x + map.width / 2) / dx, 0, columns), gz = THREE.MathUtils.clamp((z + map.depth / 2) / dz, 0, rows);
    const column = Math.min(columns - 1, Math.floor(gx)), row = Math.min(rows - 1, Math.floor(gz));
    const { i, j, size } = map.terrain ? terrainCellAt(map, column, row) : { i: column, j: row, size: 1 };
    const u = (gx - i) / size, v = (gz - j) / size;
    if (u + v <= 1) {
      read(a, i, j); read(b, i + size, j); read(c, i, j + size);
      return target.copy(a).multiplyScalar(1 - u - v).add(b.multiplyScalar(u)).add(c.multiplyScalar(v));
    }
    read(a, i + size, j + size); read(b, i, j + size); read(c, i + size, j);
    return target.copy(a).multiplyScalar(u + v - 1).add(b.multiplyScalar(1 - u)).add(c.multiplyScalar(1 - v));
  };
}

/**
 * The ground mesh. With terrain it is built tile by tile on the terrain's lattice: coarse tiles use every `ratio`-th point and
 * fine tiles every point, sharing the points they have in common, so the surface is exactly the one `terrainHeight` samples.
 * `keep` picks the tiles to build by their lattice bounds, as for a cave's floor under its roof.
 */
export function createGroundGeometry(map: MapDefinition, flatColumns = 1, flatRows = 1, keep?: (i0: number, j0: number, i1: number, j1: number) => boolean) {
  if (!map.terrain) return new THREE.PlaneGeometry(map.width, map.depth, flatColumns, flatRows).rotateX(-Math.PI / 2);
  const mesh = terrainMesh(map), { columns, rows, dx, dz, ratio, tile } = mesh;
  const cells = (visit: (i: number, j: number, step: number) => void) => {
    for (let row = 0; row < mesh.tileRows; row++) for (let column = 0; column < mesh.tileColumns; column++) {
      if (keep && !keep(column * tile, row * tile, Math.min(columns, (column + 1) * tile), Math.min(rows, (row + 1) * tile))) continue;
      const step = mesh.fine[row * mesh.tileColumns + column] ? 1 : ratio;
      for (let j = row * tile; j < Math.min(rows, (row + 1) * tile); j += step) for (let i = column * tile; i < Math.min(columns, (column + 1) * tile); i += step) visit(i, j, step);
    }
  };
  // First pass: number the lattice points the mesh uses, so the buffers are allocated once at their exact size.
  const ids = new Int32Array((columns + 1) * (rows + 1)).fill(-1), lattice: number[] = [];
  let triangles = 0;
  const use = (i: number, j: number) => { const key = j * (columns + 1) + i; if (ids[key] < 0) { ids[key] = lattice.length; lattice.push(key); } return ids[key]; };
  cells((i, j, step) => { use(i, j); use(i, j + step); use(i + step, j + step); use(i + step, j); triangles += 2; });
  const positions = new Float32Array(lattice.length * 3), uvs = new Float32Array(lattice.length * 2);
  lattice.forEach((key, id) => {
    const i = key % (columns + 1), j = (key - i) / (columns + 1);
    positions.set([i * dx - map.width / 2, terrainLatticeHeight(map, i, j), j * dz - map.depth / 2], id * 3); uvs.set([i / columns, 1 - j / rows], id * 2);
  });
  const indices = lattice.length > 65535 ? new Uint32Array(triangles * 3) : new Uint16Array(triangles * 3);
  let cursor = 0;
  cells((i, j, step) => {
    // The same diagonal and winding as a rotated PlaneGeometry.
    const a = use(i, j), b = use(i, j + step), c = use(i + step, j + step), d = use(i + step, j);
    indices.set([a, b, d, b, c, d], cursor); cursor += 6;
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeVertexNormals();
  return geometry;
}
